import { useCallback, useEffect, useMemo, useState } from "react"

import { Button, Modal } from "react-bootstrap"
import { DndProvider } from "react-dnd"
import { HTML5Backend } from "react-dnd-html5-backend"

import DroppableContainer from "./Container"
import CustomizableColumn from "./CustomizableColumn"

interface ColumnsCustomizeModalProps {
  show: boolean
  columns: CustomizableColumn[]
  storeKey?: string
  onSave: (columns: CustomizableColumn[]) => void
  onClose: () => void
}

export const saveCustomized = (key: string, columns: CustomizableColumn[]): CustomizableColumn[] => {
  if (!key) return columns

  try {
    localStorage.setItem(key, JSON.stringify(columns))
  } catch (error) {
    console.error("Failed to save column configuration:", error)
  }

  return columns
}

export const loadCustomized = (key: string, defaultColumns: CustomizableColumn[]): CustomizableColumn[] => {
  if (!key) return defaultColumns

  try {
    const dataStr = localStorage.getItem(key)
    if (!dataStr) {
      saveCustomized(key, defaultColumns)
      return defaultColumns
    }

    const savedColumns: CustomizableColumn[] = JSON.parse(dataStr)

    // Keep only saved columns that exist in defaults
    const merged = savedColumns
      .filter((savedCol) => defaultColumns.some((c) => c.name === savedCol.name))
      .map((savedCol) => {
        const defaultCol = defaultColumns.find((c) => c.name === savedCol.name)!
        return { ...defaultCol, ...savedCol }
      })

    // Add any new default columns not in saved list
    const missingDefaults = defaultColumns.filter((col) => !savedColumns.some((c) => c.name === col.name))

    return [...merged, ...missingDefaults]
  } catch (error) {
    console.error("Failed to load column configuration:", error)
    return defaultColumns
  }
}

function ColumnsCustomizeModal({
  show,
  columns: initialColumns,
  storeKey = "",
  onSave,
  onClose,
}: ColumnsCustomizeModalProps) {
  const visibleInitialColumns = useMemo(() => initialColumns.filter((column) => !column.hidden), [initialColumns])

  const [columns, setColumns] = useState<CustomizableColumn[]>(visibleInitialColumns)

  useEffect(() => {
    if (show) {
      setColumns(visibleInitialColumns)
    }
  }, [show, visibleInitialColumns])

  const handleChangeColumnsList = useCallback((newColumns: CustomizableColumn[]) => {
    setColumns(newColumns)
  }, [])

  const handleVisibilityChange = useCallback((name: string, visible: boolean) => {
    setColumns((prevColumns) => prevColumns.map((column) => (column.name === name ? { ...column, visible } : column)))
  }, [])

  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSave = useCallback(() => {
    const updatedColumns = saveCustomized(storeKey, columns)
    onSave(updatedColumns)
  }, [columns, onSave, storeKey])

  return (
    <Modal backdrop="static" size="xl" centered show={show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>CUSTOMIZE COLUMNS</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="pb-2">Select columns you wish to view. Drag and drop columns to change the viewing order.</div>
        <DndProvider backend={HTML5Backend}>
          <DroppableContainer
            columns={columns}
            onChangeColumnsList={handleChangeColumnsList}
            onVisibilityChange={handleVisibilityChange}
          />
        </DndProvider>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          CANCEL
        </Button>
        <Button variant="primary" onClick={handleSave}>
          CUSTOMIZE
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default ColumnsCustomizeModal
